import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Crown, ArrowRight, CheckCircle } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { getStripePriceIds } from '@shared/stripe-config';

interface TeamLimitUpgradeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserCount: number;
  onUpgradeSuccess?: () => void;
}

export function TeamLimitUpgradeDialog({ 
  isOpen, 
  onClose, 
  currentUserCount,
  onUpgradeSuccess 
}: TeamLimitUpgradeDialogProps) {
  const { toast } = useToast();
  const [isUpgrading, setIsUpgrading] = useState(false);

  const upgradeMutation = useMutation({
    mutationFn: async (planData: { planId: number; priceId: string }) => {
      const response = await apiRequest('/api/subscription/upgrade-checkout', {
        method: 'POST',
        body: planData
      });
      return response;
    },
    onSuccess: (data) => {
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast({
        title: "Upgrade Failed",
        description: "Failed to start the upgrade process. Please try again.",
        variant: "destructive",
      });
      setIsUpgrading(false);
    },
  });

  const handleUpgrade = (planId: number, priceId: string) => {
    setIsUpgrading(true);
    upgradeMutation.mutate({ planId, priceId });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center space-x-2 mb-2">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Users className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-lg">User Limit Reached</DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                You've reached the maximum number of users for your current plan
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Status */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-orange-900">Current Plan Limit</h4>
                <p className="text-sm text-orange-700">Maximum users reached</p>
              </div>
              <Badge variant="outline" className="border-orange-300 text-orange-700">
                Limit Exceeded
              </Badge>
            </div>
          </div>

          {/* Upgrade Recommendation */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Crown className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-purple-900 mb-2">Upgrade to Enterprise</h4>
                <div className="space-y-2 text-sm text-purple-700">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Unlimited users</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Unlimited licenses & applications</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>24/7 priority support</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Advanced analytics & reporting</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Custom integrations</span>
                  </div>
                </div>
                <div className="mt-3 pt-2 border-t border-purple-200">
                  <p className="text-lg font-semibold text-purple-900">
                    $299/month
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Message */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              To add more team members, please upgrade your plan to Enterprise.
            </p>
          </div>
        </div>

        <DialogFooter className="flex space-x-2">
          <Button variant="outline" onClick={onClose} disabled={isUpgrading}>
            Maybe Later
          </Button>
          <Button 
            onClick={() => handleUpgrade(4, getStripePriceIds().enterprise)} 
            disabled={isUpgrading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isUpgrading ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Processing...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span>Upgrade to Enterprise</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}